import { useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";

// Batch delay in ms - accumulate updates before processing
const BATCH_DELAY_MS = 1000;

/**
 * Custom hook for real-time subitem updates with batched processing
 * @param {number} projectId - The project ID to subscribe to
 * @param {function} onSubitemUpdate - Callback when a subitem is updated
 * @param {boolean} enabled - Whether the subscription is enabled
 */
export const useRealtimeSubitems = (
  projectId,
  onSubitemUpdate,
  enabled = true,
) => {
  // Refs for batching updates
  const pendingUpdates = useRef(new Map());
  const batchTimeout = useRef(null);

  // Transform database format to component format
  const transformSubitem = (payload) => ({
    id: payload.new.id,
    name: payload.new.name,
    phase: payload.new.phase,
    subitemStatus: payload.new.subitem_status,
    user: payload.new.user,
    employeeId: payload.new.employee_id,
    employee: payload.new.employee,
    order: payload.new.order,
    projectId: payload.new.project_id,
    createdAt: payload.new.created_at,
    updatedAt: payload.new.updated_at,
  });

  // Process all pending updates in a single batch
  const processBatch = useCallback(() => {
    const updates = new Map(pendingUpdates.current);
    pendingUpdates.current.clear();

    // Process each update (subitems are simple - no relationships to fetch)
    updates.forEach((payload) => {
      if (payload.new.project_id === projectId) {
        onSubitemUpdate(transformSubitem(payload));
      }
    });
  }, [projectId, onSubitemUpdate]);

  // Queue an update for batched processing
  const queueUpdate = useCallback(
    (payload) => {
      if (payload.eventType !== "UPDATE" || !payload.new) {
        return;
      }

      // Store/overwrite the update for this subitem (latest wins)
      pendingUpdates.current.set(payload.new.id, payload);

      // Reset the batch timer
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      batchTimeout.current = setTimeout(processBatch, BATCH_DELAY_MS);
    },
    [processBatch],
  );

  useEffect(() => {
    if (!enabled || !projectId || !onSubitemUpdate) {
      return;
    }

    // Subscribe to changes on the subitems table
    const channel = supabase
      .channel(`subitems-project-${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "subitems",
          filter: `project_id=eq.${projectId}`,
        },
        queueUpdate,
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.error("Realtime: Failed to subscribe to subitems");
        }
      });

    // Cleanup subscription on unmount
    return () => {
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      supabase.removeChannel(channel);
    };
  }, [projectId, enabled, queueUpdate, onSubitemUpdate]);
};
